import React from 'react';
import styles from './LicenseKeyInput.module.css';

interface LicenseKeyInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function LicenseKeyInput({
  value,
  onChange,
  disabled = false,
}: LicenseKeyInputProps) {
  // 영숫자만 추출 후 XXXX-XXXX-XXXX-XXXX-XXXX 형식으로 포맷팅
  const formatLicenseKey = (raw: string): string => {
    const alphanumeric = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const limited = alphanumeric.slice(0, 20); // 하이픈 제외 최대 20자
    const groups: string[] = [];
    for (let i = 0; i < limited.length; i += 4) {
      groups.push(limited.slice(i, i + 4));
    }
    return groups.join('-');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(formatLicenseKey(e.target.value));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 백스페이스로 하이픈 위치에서 삭제 시 이전 문자도 함께 삭제
    if (e.key === 'Backspace') {
      const input = e.currentTarget;
      const cursorPos = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;

      // 선택 범위가 없고 커서 바로 앞이 하이픈인 경우
      if (cursorPos === selectionEnd && cursorPos > 0 && value[cursorPos - 1] === '-') {
        e.preventDefault();
        // 하이픈 앞의 문자까지 제거 (하이픈 + 앞 문자 = 2자 제거)
        const before = value.slice(0, cursorPos - 2);
        const after = value.slice(cursorPos);
        const newValue = formatLicenseKey(before + after);
        onChange(newValue);

        // 커서 위치 복원
        requestAnimationFrame(() => {
          const newPos = Math.max(0, cursorPos - 2);
          input.setSelectionRange(newPos, newPos);
        });
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const input = e.currentTarget;
    const cursorPos = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? value.length;

    const before = value.slice(0, cursorPos);
    const after = value.slice(selectionEnd);
    const combined = before + pastedText + after;
    onChange(formatLicenseKey(combined));
  };

  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor="license-key">
        라이선스 키
      </label>
      <input
        id="license-key"
        type="text"
        className={styles.input}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder="WAIT-XXXX-XXXX-XXXX-XXXX"
        maxLength={24}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        disabled={disabled}
      />
      <p className={styles.hint}>
        매장에 발급된 라이선스 키를 입력하세요.
      </p>
    </div>
  );
}
